import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import { Eye, LogOut, RefreshCw, Activity, BookOpen, ArrowLeft, MapPin, X } from "lucide-react";
import LiveFeed from "@/components/shared/LiveFeed";
import TutorialLedger, { TutorialToggle } from "@/components/shared/TutorialLedger";
import DashboardSwitcher from "@/components/shared/DashboardSwitcher";
import LiveMap from "@/components/shared/LiveMap";

export default function ViewerDashboard() {
  const { session, setSession, hubData, refreshData, logout } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const nav = useNavigate();
  const { events, drivers, requests, machines, sites } = hubData;
  const [showTutorial, setShowTutorial] = useState(false);
  const [showMapPanel, setShowMapPanel] = useState(false);

  const activeDrivers = drivers.filter(d => d.status === "ACTIVE");
  const newReqs = requests.filter(r => r.status === "NEW").length;
  const inProgress = requests.filter(r => r.status === "IN_PROGRESS").length;
  const done = requests.filter(r => r.status === "DONE").length;
  const totalLiters = requests.filter(r => r.status === "DONE").reduce((s, r) => s + (r.delivered_diesel || 0), 0);

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col" data-testid="viewer-dashboard">
      <header className="bg-white px-6 py-4 flex items-center justify-between border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-700 rounded-xl flex items-center justify-center text-white shadow-lg">
            <Eye className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">{t("viewer")} Mode</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{session?.hubName}</p>
          </div>
          <DashboardSwitcher currentRole="viewer" />
        </div>
        <div className="flex items-center gap-2">
          <TutorialToggle onClick={() => setShowTutorial(!showTutorial)} />
          <button data-testid="viewer-map-btn" onClick={() => setShowMapPanel(!showMapPanel)}
            className={`p-2.5 rounded-xl transition-all border ${showMapPanel ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-100 border-slate-200 text-slate-400 hover:text-slate-900"}`}>
            <MapPin className="w-4 h-4" />
          </button>
          <button onClick={refreshData} className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { setSession(null); nav("/portal"); }} data-testid="back-to-lobby-btn"
            className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button onClick={() => { logout(); nav("/"); }}
            className="p-2.5 bg-red-50 border border-red-200 rounded-xl text-red-500 hover:bg-red-600 hover:text-slate-900 transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <TutorialLedger role="viewer" isOpen={showTutorial} onClose={() => setShowTutorial(false)} />

      {/* Local Map */}
      {showMapPanel && (
        <div className="px-4 md:px-8 py-4 bg-slate-50/80 border-b border-slate-200 animate-slideInDown" data-testid="viewer-map-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-slate-500" /> All Sites Map
            </h3>
            <button onClick={() => setShowMapPanel(false)} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
            <LiveMap sites={sites} machines={machines} requests={requests} />
          </div>
        </div>
      )}


      <main className="flex-1 p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Active Drivers", value: activeDrivers.length, color: "text-lime-600" },
            { label: "New Requests", value: newReqs, color: "text-amber-400" },
            { label: "In Progress", value: inProgress, color: "text-sky-400" },
            { label: "Total Diesel", value: `${totalLiters}L`, color: "text-lime-600" },
          ].map((stat, i) => (
            <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{stat.label}</span>
              <span className={`text-2xl font-black font-mono ${stat.color} mt-1 block`}>{stat.value}</span>
            </div>
          ))}
        </div>

        <LiveFeed events={events} />

        {/* Active Drivers */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
            <Activity className="w-3.5 h-3.5 text-lime-500" /> Active Drivers
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {drivers.map(d => (
              <div key={d.driver_name} className={`p-4 rounded-xl border flex items-center gap-3 ${d.status === "ACTIVE" ? "border-lime-300 bg-lime-500/5" : "border-slate-200 bg-white opacity-60"}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg ${d.status === "ACTIVE" ? "bg-lime-500 text-slate-950" : "bg-slate-700 text-white"}`}>
                  {d.driver_name[0]}
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-900 uppercase tracking-tight block">{d.driver_name}</span>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{d.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Requests */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
            <Activity className="w-3.5 h-3.5 text-lime-500" /> Recent Requests
          </h3>
          <div className="space-y-2">
            {requests.slice(0, 10).map(req => (
              <div key={req.id} className="p-3 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${req.status === "DONE" ? "bg-lime-500" : req.status === "IN_PROGRESS" ? "bg-amber-500 animate-pulse" : "bg-slate-500"}`} />
                  <div>
                    <span className="text-xs font-bold text-slate-800">{req.machine_number}</span>
                    <span className="text-[10px] text-slate-400 ml-2">{req.site_name}</span>
                  </div>
                </div>
                <span className="text-[10px] font-bold text-slate-400 uppercase">{req.status}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
